import { generatePath } from 'react-router-dom';
import {
  AGENT_TAB_PATH_TEMPLATE,
  AGENTS_BASE_PATH,
  BASE_AGENT_TABS,
  DEFAULT_AGENT_TAB,
  type AgentTabSpec,
} from './routeConfig';

interface AgentCustomView {
  key: string;
  displayName: string;
}

function normalizeTabKey(tabKey: string | undefined | null): string {
  if (!tabKey)
    return DEFAULT_AGENT_TAB;

  const normalized = tabKey.trim().toLowerCase();
  return normalized || DEFAULT_AGENT_TAB;
}

export function buildAgentsCatalogPath(company?: string): string {
  if (!company)
    return AGENTS_BASE_PATH;

  const query = new URLSearchParams({ company }).toString();
  return `${AGENTS_BASE_PATH}?${query}`;
}

export function buildAgentTabPath(agentKey: string, tabKey = DEFAULT_AGENT_TAB, company?: string): string {
  const safeTabKey = normalizeTabKey(tabKey);
  const path = generatePath(AGENT_TAB_PATH_TEMPLATE, {
    agentKey,
    tabKey: safeTabKey,
  });

  if (!company)
    return path;

  const query = new URLSearchParams({ company }).toString();
  return `${path}?${query}`;
}

export function getAgentTabFromPath(pathname: string): string {
  const tabMatch = pathname.match(/^\/agents\/[^/]+\/([^/?#]+)/i);
  return normalizeTabKey(tabMatch?.[1]);
}

/**
 * Builds the tab list for an agent: Overview first, then the agent's custom views (e.g. the EVA
 * worklist) in the slot the batch-era Runs tab used to occupy, then the remaining base tabs.
 */
export function buildAgentTabs(customViews?: AgentCustomView[]): AgentTabSpec[] {
  const [overview, ...rest] = BASE_AGENT_TABS;
  const tabs: AgentTabSpec[] = [overview];
  const knownKeys = new Set(BASE_AGENT_TABS.map((tab) => tab.key));

  for (const view of customViews ?? []) {
    const key = normalizeTabKey(view.key);

    if (knownKeys.has(key))
      continue;

    knownKeys.add(key);
    tabs.push({ key, label: view.displayName, custom: true });
  }

  tabs.push(...rest);

  return tabs;
}
